import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import { FORM_REFS } from '../../constants/oneOffAuthorization';
import AuthorizationAttachmentsSection from '../authorization/AuthorizationAttachmentsSection';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';

export const emptyGetsCm002aForm = (overrides = {}) => ({
  reasonForOneOff: '',
  aircraftType: '',
  aircraftReg: '',
  msn: '',
  location: '',
  requestDate: '',
  operatorName: '',
  personName: '',
  countryOfIssue: '',
  ameLicenseNo: '',
  licenseValidTill: '',
  licenseRatings: '',
  licenseCategory: '',
  maintenanceOrgNameApproval: '',
  briefMaintenanceTask: '',
  remarks: '',
  confirmedMaintenanceData: false,
  confirmedToolsEquipment: false,
  requestOriginator: {
    name: '',
    designation: '',
    date: ''
  },
  ...overrides
});

function GetsCm002aApplicationForm({
  value,
  onChange,
  readOnly = false,
  showHeader = true,
  onDownloadPdf,
  attachments,
  pendingFiles,
  attachmentsSaving = false,
  onAddAttachmentFiles,
  onRemovePendingAttachment,
  onViewAttachment,
  onDownloadAttachment,
  onDeleteAttachment
}) {
  const form = value || emptyGetsCm002aForm();
  const originator = {
    name: '',
    designation: '',
    date: '',
    ...(form.requestOriginator || {})
  };

  const set = (patch) => onChange({ ...form, ...patch });
  const setOriginator = (patch) =>
    set({ requestOriginator: { ...originator, ...patch } });

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderColor: 'text.primary',
        borderWidth: 2,
        borderStyle: 'double',
        bgcolor: 'background.paper'
      }}
    >
      {showHeader && (
        <Box sx={{ mb: 2 }}>
          <GetsBrandHeader
            title="Application for Issue of One-Off Authorization"
            subtitle={`Form: ${FORM_REFS.application} | Issue: 01 Rev 00`}
          />
          {onDownloadPdf && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: -1, mb: 1 }}>
              <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
                Download {FORM_REFS.application} PDF
              </Button>
            </Stack>
          )}
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        required
        multiline
        minRows={3}
        label="Reason for One Off Authorization"
        value={form.reasonForOneOff || ''}
        onChange={(e) => set({ reasonForOneOff: e.target.value })}
        sx={{ mb: 2 }}
        {...fieldProps}
      />

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase' }}>
          Aircraft / Location
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Aircraft Type"
              value={form.aircraftType || ''}
              onChange={(e) => set({ aircraftType: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              value={form.location || ''}
              onChange={(e) => set({ location: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Aircraft Reg"
              value={form.aircraftReg || ''}
              onChange={(e) => set({ aircraftReg: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(form.requestDate)}
              onChange={(e) => set({ requestDate: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="MSN"
              value={form.msn || ''}
              onChange={(e) => set({ msn: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Operator"
              value={form.operatorName || ''}
              onChange={(e) => set({ operatorName: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase' }}>
          Person to be Authorized
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              required
              label="Name"
              value={form.personName || ''}
              onChange={(e) => set({ personName: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Country of Issue"
              value={form.countryOfIssue || ''}
              onChange={(e) => set({ countryOfIssue: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="AME License No"
              value={form.ameLicenseNo || ''}
              onChange={(e) => set({ ameLicenseNo: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid Till"
              InputLabelProps={{ shrink: true }}
              value={dateVal(form.licenseValidTill)}
              onChange={(e) => set({ licenseValidTill: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="License Category"
              value={form.licenseCategory || ''}
              onChange={(e) => set({ licenseCategory: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="License Ratings"
              value={form.licenseRatings || ''}
              onChange={(e) => set({ licenseRatings: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Maintenance Organization Name & Approval Number"
            value={form.maintenanceOrgNameApproval || ''}
            onChange={(e) => set({ maintenanceOrgNameApproval: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Brief Details of Maintenance Task"
            value={form.briefMaintenanceTask || ''}
            onChange={(e) => set({ briefMaintenanceTask: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Remarks"
            value={form.remarks || ''}
            onChange={(e) => set({ remarks: e.target.value })}
            {...fieldProps}
          />
        </Grid>
      </Grid>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Confirmations
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.confirmedMaintenanceData)}
                disabled={readOnly}
                onChange={(e) => set({ confirmedMaintenanceData: e.target.checked })}
              />
            }
            label="Confirmed Maintenance Data availability"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.confirmedToolsEquipment)}
                disabled={readOnly}
                onChange={(e) => set({ confirmedToolsEquipment: e.target.checked })}
              />
            }
            label="Confirmed Tools & Equipment availability"
          />
        </FormGroup>
      </Box>

      <Box sx={{ mb: 2 }}>
        <AuthorizationAttachmentsSection
          attachments={
            Array.isArray(attachments)
              ? attachments
              : Array.isArray(form.applicationAttachments)
                ? form.applicationAttachments
                : []
          }
          pendingFiles={pendingFiles}
          readOnly={readOnly}
          saving={attachmentsSaving}
          onAddFiles={onAddAttachmentFiles}
          onRemovePending={onRemovePendingAttachment}
          onView={onViewAttachment}
          onDownload={onDownloadAttachment}
          onDelete={onDeleteAttachment}
        />
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase' }}>
          Request Originator
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={originator.name || ''}
              onChange={(e) => setOriginator({ name: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Designation"
              value={originator.designation || ''}
              onChange={(e) => setOriginator({ designation: e.target.value })}
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(originator.date)}
              onChange={(e) => setOriginator({ date: e.target.value })}
              {...fieldProps}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default GetsCm002aApplicationForm;
